/* eslint-disable sonarjs/no-duplicate-string */
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import {
  OPPORTUNITY_CURRENCY, PROPERTY_STATUS, TYPE_OF_TRANSACTION
} from '@modules/opportunities/entities/opportunity/const';
import {
  Input, InputGroup, InputLabel, InputDescription,
  Text,
  Flex,
} from '@huspy/briks-web';
import Select from '@components/v2/Select';
import NumberInput from '@components/NumberInput';
import { Bank } from '@huspy/briks-icons';
import { Divider, Grid } from '@huspy/styled-system/jsx';
import useFeeCalculation from '@modules/proposals/hooks/useFeeCalculation';
import { ProposalCreationClientAndMortgageForm } from '../../types';
import { BUYOUT_TRANSACTION_TYPES, EQUITY_TRANSACTION_TYPES } from '../..';
import FeeFinanceBreakdown from './features/FeeFinanceBreakdown';
import { emiratesSupportFeeFinancing } from './utils/const';
import ProposalClientAndMortgageSectionBox from '../SectionBox';
import { ProposalFormHandlers } from '../../handlers';
import { recalculateMortgageDetailsVaues } from './utils/calculations';
import {
  PROPOSAL_EXISTING_MORTGAGE_TYPE_OPTIONS,
  PROPOSAL_MORTGAGE_TYPE_OPTIONS, PROPOSAL_TRANSACTION_TYPE, PROPOSAL_TRANSACTION_TYPE_CONSTRUCTION
} from './const';

type Props = {
  form: ReturnType<typeof useForm<ProposalCreationClientAndMortgageForm>>;
  isDisabled?: boolean;
};

const {
  existingMortgageTypeHandler, transactionTypeHandler,
  landDepartmentFeeFinanceHandler,
  realEstateFeeFinanceHandler,
} = ProposalFormHandlers;

export const MortgageDetails = ({ form, isDisabled }: Props) => {
  const { t } = useTranslation();

  const {
    feesToDisplay,
    isLoading: isLoadingFees,
    conditions,
  } = useFeeCalculation({ form });

  const isAlreadyHaveMortgage = BUYOUT_TRANSACTION_TYPES.includes(form.values.mortgage_details.transaction_type);
  const isEquityType = EQUITY_TRANSACTION_TYPES.includes(form.values.mortgage_details.transaction_type);

  const { isFeeFinancingShown } = conditions;

  form.watch('mortgage_details.transaction_type', (data) => {
    if (data.previousValue !== data.value) {
      landDepartmentFeeFinanceHandler(form, false);
      realEstateFeeFinanceHandler(form, false);
    }
  });

  form.watch('mortgage_details.transaction_type', (data) => {
    const isAlreadyHaveMortgageWatch = BUYOUT_TRANSACTION_TYPES.includes(data.value);

    recalculateMortgageDetailsVaues(form, 'loan_value', isAlreadyHaveMortgageWatch);

    if (!isAlreadyHaveMortgageWatch) {
      existingMortgageTypeHandler(form, '');
      form.setFieldValue('mortgage_details.equity_release_amount', '');
    }

    if (!isEquityType) {
      form.setFieldValue('mortgage_details.outstanding_loan_amount', '');
    }
  });

  form.watch('mortgage_details.equity_release_amount', (data) => {
    if (data.value?.length && +data.value > 0) {
      const outstandingLoanAmount = +form.values.mortgage_details.loan_value
        - +data.value;
      form.setFieldValue('mortgage_details.outstanding_loan_amount', outstandingLoanAmount.toString());
    } else {
      form.setFieldValue('mortgage_details.outstanding_loan_amount', '');
    }
  });

  form.watch('mortgage_details.loan_value', (data) => {
    if (form.values.mortgage_details.equity_release_amount && +form.values.mortgage_details.equity_release_amount > 0) {
      const outstandingLoanAmount = +data.value
        - +form.values.mortgage_details.equity_release_amount;
      form.setFieldValue('mortgage_details.outstanding_loan_amount', outstandingLoanAmount.toString());
    }
  });

  return (
    <ProposalClientAndMortgageSectionBox title={ t('proposals.proposalsDetails.mortgageDetails') }>
      <Flex direction='column' gap='4'>
        <Grid
          gap='6'
          columns={ 2 }
        >
          <Select
            label={ t('common.forms.transactionType.label') }
            placeholder={ t('common.forms.transactionType.placeholder') }
            data={ form.values.property_details.property_status === PROPERTY_STATUS.ready
              ? PROPOSAL_TRANSACTION_TYPE
              : PROPOSAL_TRANSACTION_TYPE_CONSTRUCTION }
            data-test='select-transaction-type'
            disabled={ isDisabled }
            { ...form.getInputProps('mortgage_details.transaction_type') }
            onValueChange={ (e) => {
              transactionTypeHandler(form, e.value[0]);
            } }
          />

          <Select
            label={ t('common.forms.mortgageType.label') }
            placeholder={ t('common.forms.mortgageType.placeholder') }
            data={ PROPOSAL_MORTGAGE_TYPE_OPTIONS }
            data-test='select-mortgage-type'
            disabled={ isDisabled }
            { ...form.getInputProps('mortgage_details.mortgage_type') }
            onValueChange={ (e) => { form.getInputProps('mortgage_details.mortgage_type').onChange(e.value[0]); } }
          />

          {
            isAlreadyHaveMortgage ? (
              <Select
                label={ t('common.forms.existingMortgageType.label') }
                placeholder={ t('common.forms.existingMortgageType.placeholder') }
                data={ PROPOSAL_EXISTING_MORTGAGE_TYPE_OPTIONS }
                data-test='select-existing-mortgage-type'
                disabled={ isDisabled }
                { ...form.getInputProps('mortgage_details.existing_mortgage_type') }
                onValueChange={ (e) => {
                  existingMortgageTypeHandler(form, e.value[0]);
                } }
              />
            ) : null
          }
        </Grid>

        <InputGroup isError={ !!form.getInputProps('mortgage_details.property_value')?.error }>
          <InputLabel>{t('common.forms.propertyValue.label')}</InputLabel>
          <NumberInput
            name='property_value'
            placeholder={ t('common.forms.propertyValue.placeholder') }
            data-test='property-value'
            icon={ <Text size='md' color='text-tertiary'>{OPPORTUNITY_CURRENCY.AED}</Text> }
            iconPosition='right'
            disabled={ isDisabled }
            { ...form.getInputProps('mortgage_details.property_value') }
            onBlur={ () => recalculateMortgageDetailsVaues(form, 'property_value') }
          />
          <InputDescription>{form.getInputProps('mortgage_details.property_value')?.error ?? ''}</InputDescription>
        </InputGroup>

        <Grid
          gap='6'
          columns={ 2 }
        >
          <InputGroup isError={ !!form.getInputProps('mortgage_details.loan_value')?.error }>
            <InputLabel>{t('common.forms.loanValue.label')}</InputLabel>
            <NumberInput
              name='loan_value'
              placeholder={ t('common.forms.loanValue.placeholder') }
              data-test='loan-value'
              icon={ <Text size='md' color='text-tertiary'>{OPPORTUNITY_CURRENCY.AED}</Text> }
              iconPosition='right'
              disabled={ isDisabled }
              { ...form.getInputProps('mortgage_details.loan_value') }
              onBlur={ () => recalculateMortgageDetailsVaues(form, 'loan_value') }
            />
            <InputDescription>{form.getInputProps('mortgage_details.loan_value')?.error ?? ''}</InputDescription>
          </InputGroup>
          <InputGroup isError={ !!form.getInputProps('mortgage_details.ltv')?.error }>
            <InputLabel>{t('common.forms.ltv.label')}</InputLabel>
            <Input
              max={ 100 }
              min={ 0 }
              step={ 0.01 }
              name='ltv'
              type='number'
              data-test='loan-ltv'
              icon={ <Text size='md' color='text-tertiary'>%</Text> }
              iconPosition='right'
              disabled={ isDisabled }
              { ...form.getInputProps('mortgage_details.ltv') }
              onBlur={ () => recalculateMortgageDetailsVaues(form, 'ltv') }
            />
            <InputDescription>{form.getInputProps('mortgage_details.ltv')?.error ?? ''}</InputDescription>
          </InputGroup>
        </Grid>

        <Grid
          gap='6'
          columns={ 2 }
        >
          <InputGroup isError={ !!form.getInputProps('mortgage_details.downpayment_amount')?.error }>
            <InputLabel>{t('common.forms.downPayment.label')}</InputLabel>
            <NumberInput
              name='downpayment-amount'
              data-test='downpayment-amount'
              placeholder={ t('common.forms.downPayment.placeholder') }
              icon={ <Text size='md' color='text-tertiary'>{OPPORTUNITY_CURRENCY.AED}</Text> }
              iconPosition='right'
              disabled={ isDisabled || isAlreadyHaveMortgage }
              { ...form.getInputProps('mortgage_details.downpayment_amount') }
              onBlur={ () => recalculateMortgageDetailsVaues(form, 'downpayment_amount') }
            />
            <InputDescription>{form.getInputProps('mortgage_details.downpayment_amount')?.error ?? ''}</InputDescription>
          </InputGroup>
          <InputGroup isError={ !!form.getInputProps('mortgage_details.down_payment')?.error }>
            <InputLabel>%</InputLabel>
            <Input
              max={ 100 }
              min={ 0 }
              step={ 0.01 }
              name='down_payment'
              type='number'
              data-test='down-payment'
              icon={ <Text size='md' color='text-tertiary'>%</Text> }
              iconPosition='right'
              placeholder={ t('common.forms.downPayment.placeholder') }
              disabled={ isDisabled || isAlreadyHaveMortgage }
              { ...form.getInputProps('mortgage_details.down_payment') }
              onBlur={ () => recalculateMortgageDetailsVaues(form, 'downpayment') }
            />
            <InputDescription>{form.getInputProps('mortgage_details.down_payment')?.error ?? ''}</InputDescription>
          </InputGroup>
        </Grid>

        <Grid
          gap='6'
          columns={ 2 }
        >
          <InputGroup isError={ !!form.getInputProps('mortgage_details.years')?.error }>
            <InputLabel>{t('common.forms.mortgageTerm.label')}</InputLabel>
            <Input
              max={ 26 }
              min={ 0 }
              name='years'
              type='number'
              data-test='years'
              placeholder={ t('common.forms.mortgageTerm.placeholder') }
              icon={ <Text size='md' color='text-tertiary'>{t('common.forms.mortgageTerm.placeholder')}</Text> }
              iconPosition='right'
              disabled={ isDisabled }
              { ...form.getInputProps('mortgage_details.years') }
            />
            <InputDescription>{form.getInputProps('mortgage_details.years')?.error ?? ''}</InputDescription>
          </InputGroup>
          <InputGroup isError={ !!form.getInputProps('mortgage_details.months')?.error }>
            <InputLabel color='transparent'>/</InputLabel>
            <Input
              max={ 11 }
              min={ 0 }
              name='months'
              type='number'
              data-test='months'
              placeholder={ t('common.forms.mortgageTermMonths.placeholder') }
              icon={ <Text size='md' color='text-tertiary'>{t('common.forms.mortgageTermMonths.placeholder')}</Text> }
              iconPosition='right'
              disabled={ isDisabled }
              { ...form.getInputProps('mortgage_details.months') }
            />
            <InputDescription>{form.getInputProps('mortgage_details.months')?.error ?? ''}</InputDescription>
          </InputGroup>
        </Grid>

        <Grid
          gap='6'
          columns={ 2 }
        >

          {isEquityType ? (
            <InputGroup isError={ !!form.getInputProps('mortgage_details.equity_release_amount')?.error }>
              <InputLabel>{t('common.forms.equityReleaseAmount.label')}</InputLabel>
              <NumberInput
                name='equity_release_amount'
                data-test='equity-release-amount'
                placeholder={ t('common.forms.equityReleaseAmount.placeholder') }
                icon={ <Text size='md' color='text-tertiary'>{OPPORTUNITY_CURRENCY.AED}</Text> }
                iconPosition='right'
                disabled={ isDisabled }
                { ...form.getInputProps('mortgage_details.equity_release_amount') }
              />
              <InputDescription>
                {form.getInputProps('mortgage_details.equity_release_amount')?.error ?? ''}
              </InputDescription>
              <InputDescription>{t('proposals.proposalsDetails.optional')}</InputDescription>
            </InputGroup>
          ) : null}

          {form.values.mortgage_details.transaction_type === TYPE_OF_TRANSACTION.buyoutEquity ? (
            <InputGroup isError={ !!form.getInputProps('mortgage_details.outstanding_loan_amount')?.error }>
              <InputLabel>{t('common.forms.outstandingLoanAmount.label')}</InputLabel>
              <NumberInput
                name='outstanding_loan_amount'
                data-test='outstanding-loan-amount'
                placeholder={ t('common.forms.outstandingLoanAmount.placeholder') }
                icon={ <Text size='md' color='text-tertiary'>{OPPORTUNITY_CURRENCY.AED}</Text> }
                iconPosition='right'
                disabled
                { ...form.getInputProps('mortgage_details.outstanding_loan_amount') }
              />
              <InputDescription>
                {form.getInputProps('mortgage_details.outstanding_loan_amount')?.error ?? ''}
              </InputDescription>
            </InputGroup>
          ) : null}
        </Grid>

        <Flex align='center' style={ { display: isFeeFinancingShown ? 'flex' : 'none' } }>
          <Flex gap='8px' align='center'>
            <Bank />
            <Text size='lg' weight='medium' color='text-secondary' textWrap='nowrap' mr='4'>
              {t('proposals.proposalsDetails.feeFinancingTitle')}
            </Text>
          </Flex>
          <Divider h='1' />
        </Flex>
        {
          isFeeFinancingShown ? (
            <FeeFinanceBreakdown
              isRealEstateFeeFinanced={ form.getInputProps('mortgage_details.is_real_estate_fee_financed').value }
              isLandDepartmentFeeFinanced={ form.getInputProps('mortgage_details.is_land_department_fee_financed').value }
              loanAmount={ Number(form.getInputProps('mortgage_details.loan_value').value) }
              canBeFeeFinanced={ emiratesSupportFeeFinancing.has(form.values.property_details.state) }
              feesAmount={ feesToDisplay }
              currency={ OPPORTUNITY_CURRENCY.AED }
              landDepartmentFeeFinanceHandler={ (value) => landDepartmentFeeFinanceHandler(form, value) }
              realEstateFeeFinanceHandler={ (value) => realEstateFeeFinanceHandler(form, value) }
              isLoading={ isLoadingFees }
            />
          ) : null
        }
      </Flex>
    </ProposalClientAndMortgageSectionBox>
  );
};
